import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import {
  Card,
  FormRow,
  InputDatePicker,
  NumberInput,
  Select,
  type SelectRef,
  TextField,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type Contact,
  useCreateClientMutation,
  useGetBrandContactsLazyQuery,
  useGetPmLazyQuery,
} from "@src/__generated__/graphql";
import { ProjectTypeEnum } from "@src/__generated__/urql-graphql";
import { ModalBrand } from "@src/components/modules/brands/ModalBrand";
import type { TBrandFillFromClient } from "@src/components/modules/brands/ModalBrand/ModalBrand";
import { ProjectCategorySelect } from "@src/components/ui-kit/ProjectCategorySelect";
import { useSetEditorContent } from "@src/components/ui-kit/TextEditor/hooks/useSetContent";
import {
  TextEditor,
  type TextEditorRef,
} from "@src/components/ui-kit/TextEditor/TextEditor";
import {
  AddTitle,
  ModalContactPerson,
} from "@src/components/widgets/Modals/ModalContactPerson";
import { onError } from "@src/utils/apollo";
import { can, cannot } from "@src/utils/components/permissions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import {
  fieldNumberModelToInputProps,
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { required } from "@src/utils/forms/validators";
import { useStore } from "@src/utils/hooks";
import { getQueryParamOptional } from "@src/utils/routing";
import { UserSelect } from "@src/widgets/UserSelect";
import { FieldState, FormState } from "formstate";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import type React from "react";
import {
  type FormEvent,
  type FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ModalClient } from "../../../clients/detail/modals/client/ModalClient";
import { FormProjectCtx } from "./StoreProjectForm";

export const FormProjectCmp: FunctionComponent<
  React.PropsWithChildren<{
    onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
    submitTitle: string;
    loading: boolean;
    isEdit?: boolean;
    onCancel: () => void;
    isTemplate?: boolean;
  }>
> = observer(function FormProjectCmp({
  onSubmit,
  isEdit,
  loading,
  submitTitle,
  onCancel,
  isTemplate,
}) {
  const { authStore, workspaceStore } = useStore();
  const store = useContext(FormProjectCtx);
  const contactModal = useDisclosure();
  const { query } = useRouter();
  const brandSelectRef = useRef<SelectRef>(null);
  const contactSelectRef = useRef<SelectRef>(null);
  const addBrandModal = useDisclosure();
  const addClientModal = useDisclosure();
  const editorRef = useRef<TextEditorRef>(null);
  const [brandFillData, setBrandFillData] = useState<
    (TBrandFillFromClient & { clientId?: string }) | undefined
  >();
  const { pathname } = useRouter();
  const state = useLocalObservable(() => ({
    isEditForm: false,

    categoryForm: new FormState({
      name: new FieldState<string | undefined>(undefined).validators(required),
    }),
    get otherProjectManagerOptions() {
      return (
        store.projectManagerOptions.filter(
          (pm) => pm.value !== store.form.project_manager_id.$,
        ) || []
      );
    },
  }));

  useEffect(() => {
    store.form.other_project_manager_ids.reset(
      store.form.other_project_manager_ids.$.filter(
        (pm) => pm !== store.form.project_manager_id.$,
      ),
    );
  }, [store.form.project_manager_id.$]);

  const [getContacts, { refetch: refetchContacts }] =
    useGetBrandContactsLazyQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        store.assignBrandContactOptions(data);
      },
    });

  const { brand } = store;

  const [getPM] = useGetPmLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const onCreateContact = (contact: Pick<Contact, "id">) => {
    refetchContacts?.();
    store.form.contact_id.onChange(contact.id);
  };

  useEffect(() => {
    state.isEditForm = pathname === "/projects/edit";
    if (!state.isEditForm && authStore.user?.id) {
      if (
        cannot("project_create_all") ||
        (can("project_create_all") && can("user_haveProjectsAssigned"))
      ) {
        store.form.project_manager_id.onChange(authStore.user.id);
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (store.form.project_manager_id.$) {
      getPM({ variables: { id: store.form.project_manager_id.$ } });
    }
  }, [store.form.project_manager_id.$]);

  useEffect(() => {
    const id = store.form.brand_id.$;
    if (id) {
      getContacts({ variables: { id } });
    }
  }, [store.form.brand_id.$]);

  useEffect(() => {
    const titleFromUrl = getQueryParamOptional("title", query);
    const budgetIdFromUrl = getQueryParamOptional("budget_id", query);
    const brandIdFromUrl =
      getQueryParamOptional("brandId", query) ||
      getQueryParamOptional("brand_id", query);
    if (!state.isEditForm) {
      authStore.user?.team &&
        store.form.team_id.onChange(authStore.user.team.id);
    }
    if (brandIdFromUrl) {
      store.form.brand_id.onChange(brandIdFromUrl);
    }
    if (budgetIdFromUrl) {
      store.form.budget_id.onChange(budgetIdFromUrl);
      store.form.project_type.onChange(ProjectTypeEnum.FixedPrice);
    }
    if (titleFromUrl) {
      store.form.title.onChange(titleFromUrl);
    }
  }, []);

  useSetEditorContent({
    ref: editorRef?.current,
    note: store.form.note.value,
  });

  const [createClient, createClientReq] = useCreateClientMutation({
    onCompleted({ createClient }) {
      const clientNameSplit = createClient.name.split(" ");

      setBrandFillData({
        name: createClient.name,
        clientId: createClient.id,
        code:
          clientNameSplit.length > 1
            ? clientNameSplit.reduce((prev, word) => {
                if (!word[0]) return prev;

                return prev + word[0].toUpperCase();
              }, "")
            : clientNameSplit?.[0].substring(0, 2).toUpperCase(),
      });
    },
    ...onError(),
  });

  const handleBrandModalClose = () => {
    addBrandModal.onClose();
    setBrandFillData(undefined);
  };

  const handleCreateClient = () => {
    addBrandModal.onClose();
    addClientModal.onOpen();
  };

  const handleClientModalClose = () => {
    addClientModal.onClose();
    addBrandModal.onOpen();
  };

  return (
    <form data-testid="form-project" onSubmit={onSubmit}>
      <Stack
        flexDir={{
          base: "column",
          md: "row",
        }}
        spacing="6"
      >
        <Box
          w={{
            base: "full",
            md: "45%",
          }}
          maxW={{
            base: "full",
            md: "45%",
          }}
        >
          <Card sectioned>
            <h1 className="text-3xl font-semibold mb-12">
              <Trans>Project details</Trans>
            </h1>

            <FormRow title={<Trans>Project name</Trans>}>
              <TextField
                id="field-project-title"
                {...fieldToInputProps(store.form.title)}
                type="text"
              />
            </FormRow>

            <FormRow title={<Trans>Project type</Trans>}>
              <FormControl
                isInvalid={
                  !store.canChangeProjectType ||
                  !store.canChangeFromTimeMaterialsToFixedPrice
                }
              >
                <Select
                  {...fieldToSelectProps(store.form.project_type, [
                    {
                      value: ProjectTypeEnum.FixedPrice,
                      label: t`Fixed-price`,
                    },
                    {
                      value: ProjectTypeEnum.TimeMaterials,
                      label: t`Time & Materials`,
                    },
                  ])}
                  isDisabled={
                    !store.canChangeProjectType ||
                    !store.canChangeFromTimeMaterialsToFixedPrice ||
                    !!store.form.budget_id.value
                  }
                />
                {!store.canChangeProjectType && (
                  <FormErrorMessage
                    title={t`Project has multiple budget items, therefore type cannot be changed.`}
                  >
                    <Trans>
                      Project has multiple budget items, therefore type cannot
                      be changed.
                    </Trans>
                  </FormErrorMessage>
                )}
                {!store.canChangeFromTimeMaterialsToFixedPrice && (
                  <FormErrorMessage
                    title={t`Project has tasks, therefore cannot be changed to Fixed-price.`}
                  >
                    <Trans>
                      Project has tasks, therefore cannot be changed to
                      Fixed-price.
                    </Trans>
                  </FormErrorMessage>
                )}
              </FormControl>
            </FormRow>

            <FormRow title={t`Project duration`}>
              <HStack align="start" spacing="4">
                <InputDatePicker
                  w="full"
                  disableDatesAfter={store.form.expected_end_date.$}
                  error={store.form.start_date.error}
                  selected={store.form.start_date.$}
                  onChange={(val) => {
                    store.form.start_date.onChange(val?.start);
                  }}
                />
                {!isTemplate && (
                  <InputDatePicker
                    w="full"
                    disableDatesBefore={store.form.start_date.$}
                    error={store.form.expected_end_date.error}
                    selected={store.form.expected_end_date.$}
                    onChange={(val) => {
                      store.form.expected_end_date.onChange(val?.start);
                    }}
                  />
                )}
              </HStack>
            </FormRow>

            <FormRow title={t`Project category`}>
              <ProjectCategorySelect
                data-testid="form-project-category-select"
                {...fieldToSelectProps(store.form.category_ids, [])}
              />
            </FormRow>

            <FormRow title={t`Billing`}>
              <WrapComponent
                if={!!isEdit}
                with={(children) => (
                  <Tooltip
                    label={
                      cannot("project_accessAccountingAttributes")
                        ? t`Only the Financier can change the project into non-billable`
                        : t`Changing the billing status to Billable will retroactively affect the project's financial results.`
                    }
                    shouldWrapChildren
                  >
                    {children}
                  </Tooltip>
                )}
              >
                <HStack spacing="2">
                  <FormControl
                    w="fit-content"
                    isInvalid={store.showProBonoError}
                  >
                    <Switch
                      isChecked={store.form.billable.value}
                      onChange={({ target }) => {
                        store.form.billable.onChange(target.checked);
                      }}
                    />
                  </FormControl>
                  <Text color="gray" fontSize="xs">
                    {store.form.billable.$ ? (
                      <Trans>The project will be marked as billable</Trans>
                    ) : (
                      <Trans>
                        The project will be marked as non-billable (Pro bono)
                      </Trans>
                    )}
                  </Text>
                </HStack>
              </WrapComponent>
            </FormRow>

            {isEdit && store.billabilityChanged && (
              <FormRow>
                <HStack spacing="2">
                  <Switch
                    isChecked={store.form.propagate_billable.value}
                    onChange={({ target }) => {
                      store.form.propagate_billable.onChange(target.checked);
                    }}
                  />
                  <Text color="gray" fontSize="xs">
                    <Trans>
                      Should we change billing category of all existing time
                      entries and tasks in this project?
                    </Trans>
                  </Text>
                </HStack>
              </FormRow>
            )}

            <FormRow title={<Trans>Note</Trans>}>
              <TextEditor
                ref={editorRef}
                attachments={[]}
                {...fieldToInputProps(store.form.note)}
                borderColor="grey.200"
                px="3"
                borderRadius="base"
                minHeight="60px"
                isEditable
                borderWidth="thin"
                transition="border-color 150ms ease-in"
                _focusWithin={{
                  borderColor: "purple.500",
                }}
              />
            </FormRow>
          </Card>
        </Box>

        <Box
          w={{
            base: "full",
            md: "55%",
          }}
          maxW={{
            base: "full",
            md: "55%",
          }}
        >
          <Stack spacing="6">
            <Card sectioned>
              <h1 className="text-3xl font-semibold mb-10">
                <Trans>Client</Trans>
              </h1>

              <FormRow title={<Trans>Brand</Trans>}>
                <Select
                  isDisabled={store.isBrandSelectDisabled}
                  ref={brandSelectRef}
                  topExtraContent={
                    <Button
                      p="3"
                      onClick={() => {
                        addBrandModal.onOpen();
                        brandSelectRef.current?.onMenuClose();
                      }}
                      variant="link"
                    >
                      <Trans>+ Add new brand</Trans>
                    </Button>
                  }
                  closeMenuOnSelect
                  placeholder={t`Select brand`}
                  value={store.form.brand_id.value}
                  onChange={(val) => {
                    store.form.brand_id.onChange(val);
                    if (isEdit) return;
                    store.fetchRateCards(val);
                    store.form.rate_card.reset();
                  }}
                  options={store.clientOptions}
                  error={store.form.brand_id.error}
                />
              </FormRow>

              <FormRow title={t`Rate Card`}>
                <Tooltip
                  isDisabled={!isEdit}
                  label={t`Rate card can be changed in project settings.`}
                >
                  <Box>
                    <Select
                      isClearable
                      value={store.form.rate_card.value}
                      options={store.rateCardOptions}
                      onChange={store.form.rate_card.onChange}
                      isDisabled={isEdit}
                    />
                  </Box>
                </Tooltip>
                {!isEdit && store.showExchangeRateInput && (
                  <Box mt="2">
                    <NumberInput
                      currency
                      prefix={`1 ${workspaceStore.settings?.currency.code} =`}
                      prefixStyles={{ w: 16 }}
                      inputStyle={{ paddingLeft: 16 }}
                      suffix={store.selectedRateCard?.currency_iso ?? ""}
                      {...fieldNumberModelToInputProps(store.exchangeRate)}
                    />
                  </Box>
                )}
              </FormRow>

              <FormRow title={t`Contact person`}>
                <UserSelect
                  ref={contactSelectRef}
                  isDisabled={store.form.brand_id.$ === ""}
                  placeholder={t`Select contact person`}
                  topExtraContent={
                    <Button
                      p="3"
                      onClick={() => {
                        contactModal.onOpen();
                        contactSelectRef.current?.onMenuClose();
                      }}
                      variant="link"
                    >
                      <Trans>+ Add new contact person</Trans>
                    </Button>
                  }
                  {...fieldToSelectProps(
                    store.form.contact_id,
                    store.contactOptions,
                  )}
                />
              </FormRow>
              <FormRow title={t`Client access`}>
                <UserSelect
                  userType="client"
                  isMulti
                  placeholder={t`Select clients`}
                  projectId={getQueryParamOptional("id", query)}
                  {...fieldToInputProps(store.form.client_ids)}
                />
              </FormRow>
            </Card>

            {brand && (
              <ModalContactPerson
                {...contactModal}
                title={<AddTitle brand={brand} />}
                brandId={brand.id}
                onSubmit={onCreateContact}
              />
            )}

            <Card sectioned>
              <h1 className="text-3xl font-semibold mb-10">
                <Trans>Team & Project manager</Trans>
              </h1>

              <FormRow title={<Trans>Team</Trans>}>
                <Tooltip
                  label={
                    store.isFinancier
                      ? t`Changing this field will affect financial data.`
                      : t`This field is only editable by Financier, CFO or Admin. To change this field, please contact one of the mentioned roles.`
                  }
                >
                  <Box>
                    <Select
                      isDisabled={state.isEditForm && !store.isFinancier}
                      placeholder={t`Select Team`}
                      {...fieldToSelectProps(
                        store.form.team_id,
                        store.teamOptions,
                      )}
                    />
                  </Box>
                </Tooltip>
              </FormRow>

              <FormRow title={t`Main project manager`}>
                <UserSelect
                  placeholder={t`Select project manager`}
                  {...fieldToSelectProps(
                    store.form.project_manager_id,
                    store.projectManagerOptions,
                  )}
                />
              </FormRow>

              <FormRow title={t`Other project managers`}>
                <UserSelect
                  isMulti
                  placeholder={t`Add project manager`}
                  {...fieldToSelectProps(
                    store.form.other_project_manager_ids,
                    state.otherProjectManagerOptions,
                  )}
                />
              </FormRow>
            </Card>
          </Stack>

          <HStack justify="end" mt="6" spacing="3">
            <Button colorScheme="grey" onClick={onCancel} variant="outline">
              <Trans>Cancel</Trans>
            </Button>

            <Button
              id="button-create-project"
              isLoading={loading}
              type="submit"
            >
              {submitTitle}
            </Button>
          </HStack>
        </Box>
      </Stack>

      <ModalBrand
        {...addBrandModal}
        onSubmit={(payload) => {
          store.form.brand_id.onChange(payload.id);
          handleBrandModalClose();
          store.refetchProjectCreateData?.();
        }}
        onClose={handleBrandModalClose}
        brandFill={brandFillData}
        clientId={brandFillData?.clientId}
        onCreateClientClick={handleCreateClient}
        preventRedirect
      />
      <ModalClient
        {...addClientModal}
        title={t`Add client`}
        isLoading={createClientReq.loading}
        onClose={handleClientModalClose}
        onSubmit={async (val) => {
          addBrandModal.onOpen();
          await createClient({ variables: { input: { ...val } } });
        }}
      />
    </form>
  );
});
